
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import TreeMapFilterable from '@/components/reports-v3/components/filterables/TreeMapFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import {
	PermissionsGroup,
	ValueText,
} from '@/store/models.def';
import { getTableName } from '@/worker/db/sqlHelpers';
import * as echarts from 'echarts';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';
import gdbx from '@/store/modules/gdbx';

@Component({
	components: {
		TreeMapFilterable,
		SelectFilter,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class PurchaseAnalysisTreeMap extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['purchases'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange', 'suppliers'];

	public result: any = [];
	public series: echarts.ECharts[] | any = [];

	public selectedGrouping: any = null;

	public get groupingList(): Array<ValueText<string>> {
		const list = [
			{ text: 'Stock Item', value: 'stockItems' },
			{ text: 'Stock Group', value: 'stockGroup' },
		];

		return list;
	}

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedSuppliers,
			selectedGrouping,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedSuppliers,
			selectedGrouping,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const queryHead = await this._ensureTablesLoaded(
			['purchases', 'purchaseReturn', 'supplierTrades'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.5);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const purchasesTable = getTableName('purchases');
		const returnTable = getTableName('purchaseReturn');
		const tradesTable = getTableName('supplierTrades');

		const dateFilter = `TRY_CAST("DOCDATE" AS BIGINT) >= ${this.selectedDateRange[0]} AND TRY_CAST("DOCDATE" AS BIGINT) <= ${this.selectedDateRange[1]}`;

		let supplierFilter = '';
		if (this.selectedSuppliers && this.selectedSuppliers.length > 0) {
			const list = this.selectedSuppliers.map((s: string) => `'${escapeStr(s)}'`).join(',');
			supplierFilter = ` AND "CODE" IN (${list})`;
		}

		const phDocTypes = this.includeDebitNote ? ['PI', 'CP', 'SD'] : ['PI', 'CP'];
		const phDocFilter = ` AND "DOCTYPE" IN (${phDocTypes.map((t) => `'${t}'`).join(',')})`;

		const treeColors = [
			'#8975ea', '#acbeff', '#accdff', '#acdaff', '#cdc8f9', '#c7b5e2',
		];

		// Determine grouping column
		const groupCol = this.selectedGrouping === 'stockGroup' ? '"STOCKGROUP"' : '"ITEMCODE"';
		const nameMap = this.selectedGrouping === 'stockGroup'
			? gdbx.stockGroupNames : gdbx.stockItemNames;

		// Query purchases grouped by item/group
		const purchaseQuery = queryHead.rawQuery(`
			SELECT ${groupCol} as grp,
				${this.selectedGrouping === 'stockGroup' ? `"ITEMCODE" as item,` : ''}
				SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
			FROM ${purchasesTable}
			WHERE ${dateFilter}${phDocFilter}${supplierFilter}
			GROUP BY ${groupCol}${this.selectedGrouping === 'stockGroup' ? ', "ITEMCODE"' : ''}
		`);

		// Query CN if needed
		const cnQuery = this.includeCreditNote
			? queryHead.rawQuery(`
				SELECT ${groupCol} as grp,
					${this.selectedGrouping === 'stockGroup' ? `"ITEMCODE" as item,` : ''}
					SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
				FROM ${returnTable}
				WHERE ${dateFilter}${supplierFilter}
				GROUP BY ${groupCol}${this.selectedGrouping === 'stockGroup' ? ', "ITEMCODE"' : ''}
			`)
			: Promise.resolve([]);

		// AP Document amount (if AP mode)
		let apDocPromise: Promise<number> = Promise.resolve(0);
		if (!this.slPhModuleOnly) {
			apDocPromise = (async () => {
				const apDocTypes = this.includeDebitNote ? ['PI', 'SD'] : ['PI'];
				const apDocFilter = ` AND "DOCTYPE" IN (${apDocTypes.map((t) => `'${t}'`).join(',')})`;

				const [apRes, apCnRes, phRes, phCnRes] = await Promise.all([
					queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
						FROM ${tradesTable}
						WHERE ${dateFilter}${apDocFilter}${supplierFilter}
					`),
					this.includeCreditNote ? queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total
						FROM ${tradesTable}
						WHERE ${dateFilter} AND "DOCTYPE" = 'SC'${supplierFilter}
					`) : Promise.resolve([{ total: 0 }]),
					queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
						FROM ${purchasesTable}
						WHERE ${dateFilter}${phDocFilter}${supplierFilter}
					`),
					this.includeCreditNote ? queryHead.rawQuery(`
						SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total
						FROM ${returnTable}
						WHERE ${dateFilter}${supplierFilter}
					`) : Promise.resolve([{ total: 0 }]),
				]);
				const apAmt = ((apRes[0] && Number(apRes[0].total)) || 0)
					- ((apCnRes[0] && Number(apCnRes[0].total)) || 0);
				const phAmt = ((phRes[0] && Number(phRes[0].total)) || 0)
					- ((phCnRes[0] && Number(phCnRes[0].total)) || 0);
				return apAmt - phAmt;
			})();
		}

		const [purchaseRows, cnRows, apDocumentAmount] = await Promise.all([
			purchaseQuery, cnQuery, apDocPromise,
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.9, 1);

		if (this.selectedGrouping === 'stockGroup') {
			// Build nested: stockGroup -> stockItems
			const groupMap = new Map<string, Map<string, number>>();
			for (const row of purchaseRows) {
				const grp = row.grp || '';
				const item = row.item || '';
				if (!groupMap.has(grp)) { groupMap.set(grp, new Map()); }
				const items = groupMap.get(grp)!;
				items.set(item, (items.get(item) || 0) + (Number(row.total) || 0));
			}

			const cnGroupMap = new Map<string, Map<string, number>>();
			for (const row of cnRows) {
				const grp = row.grp || '';
				const item = row.item || '';
				if (!cnGroupMap.has(grp)) { cnGroupMap.set(grp, new Map()); }
				const items = cnGroupMap.get(grp)!;
				items.set(item, (items.get(item) || 0) + (Number(row.total) || 0));
			}

			this.result = [];
			groupMap.forEach((items, grp) => {
				let groupTotal = 0;
				items.forEach((v) => { groupTotal += v; });
				let cnGroupTotal = 0;
				const cnItems = cnGroupMap.get(grp);
				if (cnItems) {
					cnItems.forEach((v) => { cnGroupTotal += v; });
				}

				const children: any[] = [];
				items.forEach((amt, itemCode) => {
					const cnAmt = this.includeCreditNote && cnItems
						? (Number(cnItems.get(itemCode)) || 0) : 0;
					children.push({
						name: gdbx.stockItemNames[itemCode] || itemCode,
						value: amt - cnAmt,
						color: treeColors,
						colorSaturation: [0.9, 0.4],
					});
				});

				this.result.push({
					name: nameMap[grp] || grp,
					value: groupTotal - (this.includeCreditNote ? cnGroupTotal : 0),
					color: treeColors,
					colorSaturation: [0.9, 0.4],
					children,
				});
			});
		} else {
			// Flat: stockItems
			const itemMap = new Map<string, number>();
			for (const row of purchaseRows) {
				const code = row.grp || '';
				itemMap.set(code, (itemMap.get(code) || 0) + (Number(row.total) || 0));
			}

			const cnItemMap = new Map<string, number>();
			for (const row of cnRows) {
				const code = row.grp || '';
				cnItemMap.set(code, (cnItemMap.get(code) || 0) + (Number(row.total) || 0));
			}

			this.result = [];
			itemMap.forEach((amt, code) => {
				const cnAmt = this.includeCreditNote ? (Number(cnItemMap.get(code)) || 0) : 0;
				this.result.push({
					name: nameMap[code] || code,
					value: amt - cnAmt,
					color: treeColors,
					colorSaturation: [0.9, 0.4],
				});
			});
		}

		// Add AP Document group in AP mode
		if (!this.slPhModuleOnly && apDocumentAmount !== 0) {
			this.result.push({
				name: 'Supplier Module Documents',
				value: apDocumentAmount,
				color: [
					'#f5a623', '#f8c471', '#fad7a0', '#fdebd0', '#fef9e7', '#fff5eb',
				],
				colorSaturation: [0.9, 0.4],
				children: [],
			});
		}

		this.result = this.result.filter((item: any) => item.value > 0);

		this.series = [{
			name: 'Purchase Analysis',
			type: 'treemap',
			roam: 'move',
			width: '100%',
			drillDownIcon: '',
			label: { color: '#000' },
			itemStyle: { gapWidth: 1, borderWidth: 1 },
			levels: [
				{
					itemStyle: { borderWidth: 2, gapWidth: 2 },
					upperLabel: { show: false, fontWeight: 'bold', color: '#000' },
					emphasis: {
						itemStyle: { borderWidth: 0, gapWidth: 2 },
						upperLabel: { show: true, fontWeight: 'bold', color: '#000' },
					},
				},
				{
					colorSaturation: [0.3, 0.6],
					itemStyle: { borderColorSaturation: 0.7, gapWidth: 2, borderWidth: 2 },
					upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					emphasis: {
						itemStyle: { borderWidth: 0, gapWidth: 1 },
						upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					},
				},
				{
					itemStyle: { gapWidth: 1, borderWidth: 1 },
					upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					emphasis: {
						itemStyle: { borderWidth: 0, gapWidth: 1 },
						upperLabel: { show: true, color: '#000', fontWeight: 'bold' },
					},
				},
			],
			data: this.result,
		}];

		this.saveHistory('result', 'series');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}
}
